/**
 * Dispatch registry — one handler per `MdmCommandAction.type`. Spec §5.3.
 *
 * Lifted from scio3/apps/staff during the staff-app monorepo carve-out. Deltas:
 *   - Imports the 6 cross-tenant mutations from
 *     `@staff-app/graphql-client/operations/proxy` (renamed `staffProxy*`)
 *     instead of the customer-side operation names. The argument shapes
 *     match the customer-side equivalents 1:1; the staff-api resolver
 *     resolves the target account from the input device(s), rejects
 *     multi-account batches, and forwards to open-api over HTTP with
 *     `Authorization: Bearer OPS_STAFF_API_BACKEND_TOKEN`.
 *   - i18n dropped — staff app is English-only. Strings are inlined.
 *   - `openChildDialog` is provided by the caller (drawer) instead of a
 *     dialog store — staff console has no global dialog orchestrator.
 *   - `parseMdmError` is inlined as a minimal fallback since apps/web does
 *     not import the scio3 `screens/utils/mdm-error` helper.
 *
 * Each handler consumes:
 *   - `cmd`     : the catalog definition (wire command, feature key, dialog id)
 *   - `ctx`     : the `MdmDispatchContext` (mutations + teamId + refetch)
 *   - `targets` : single device OR an array (bulk)
 */
import { useMemo } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { toast } from 'sonner';
import type { MdmCommandAction, MdmCommandDefinition } from '@staff-app/shared-types';
import {
  STAFF_PROXY_ADD_COMMAND_EXECUTION,
  STAFF_PROXY_ADD_COMMAND_EXECUTION_GROUP,
  STAFF_PROXY_GET_REMOTE_CONTROL_LINK,
  STAFF_PROXY_MDM_COMMAND,
  STAFF_PROXY_PLATFORM_API_COMMAND,
  STAFF_PROXY_TESTING_IDENTIFY_SCREEN,
  STAFF_PROXY_TOGGLE_DEVICE_FEATURE,
} from '@staff-app/graphql-client/operations/proxy';
import { useTeamStore } from '@/stores/team-store';
import type { MdmDevice } from '@/features/mdm-devices/hooks/useStaffCustomerDevices';

// ---------------------------------------------------------------------------
// Dispatch context
// ---------------------------------------------------------------------------

export interface MdmDispatchContext {
  device: MdmDevice;
  teamId: string | null;
  refetchDevice?: () => void;
  /**
   * Opens a child dialog — staff drawer wires this to its own local dialog
   * state (timezone, wifi, device info, command history). No global store
   * in the staff app by design.
   */
  openChildDialog: (dialogId: string, context?: Record<string, unknown>) => void;
  mutations: {
    /** Resolves to open-api's parsed reply — needs-response commands (Storage Details) carry their
     *  inline result here (`{ status, data: { value, free_value }, cmdId }`); others resolve null. */
    addCommandExecution: (vars: {
      deviceId: string;
      command: string;
      value?: string;
    }) => Promise<unknown>;
    addCommandExecutionGroup: (vars: {
      deviceIds: string[];
      command: string;
      value?: string;
    }) => Promise<void>;
    mdmCommand: (vars: { deviceIds: string[]; command: string; value?: string }) => Promise<void>;
    toggleFeature: (vars: {
      deviceIds: string[];
      featureKey: string;
      featureValue: boolean;
    }) => Promise<void>;
    fetchRemoteLink: (deviceId: string) => Promise<string | null>;
    platformApiCommand: (vars: {
      deviceId: string;
      operation: string;
      args?: Record<string, unknown>;
    }) => Promise<{ ok: boolean; data?: unknown; error?: string | null }>;
    /**
     * Flash the screen via the dedicated `identifyScreen` mutation (content swap to a flash-asset
     * template) — NOT addCommandExecution. Staff routes it through
     * `staffProxyTestingIdentifyScreen`: OptiSigns Testing account devices, or — when the context
     * carries the customer drawer's `accountId` — a device of that customer account.
     */
    identifyScreen: (vars: { deviceIds: string[] }) => Promise<void>;
  };
}

export type MdmActionTarget = MdmDevice | MdmDevice[];

export type MdmActionHandler = (
  cmd: MdmCommandDefinition,
  ctx: MdmDispatchContext,
  targets?: MdmActionTarget,
) => Promise<void>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getDeviceIds(ctx: MdmDispatchContext, targets?: MdmActionTarget): string[] {
  if (!targets) return [ctx.device._id];
  if (Array.isArray(targets)) return targets.map((d) => d._id);
  return [targets._id];
}

function isBulk(targets?: MdmActionTarget): boolean {
  return Array.isArray(targets);
}

/** "REMOTE_CONTROL" → "remoteControl" (the GraphQL enum key → the stored `device.feature` key). */
function enumKeyToCamel(key: string): string {
  return key.toLowerCase().replace(/_([a-z0-9])/g, (_m, c: string) => c.toUpperCase());
}

/**
 * Current value of a device feature flag. The catalog carries the GraphQL enum KEY
 * (`REMOTE_CONTROL`) while open-api stores the enum VALUE (`feature.remoteControl`) — so the old
 * `toLowerCase()` lookup ("remote_control") never matched and the toggle always sent `true`. Reads
 * the camelCase key first, then the raw and lower-cased key for older shapes.
 */
export function readFeatureFlag(
  feature: Record<string, unknown> | null | undefined,
  featureKey: string,
): boolean {
  if (!feature) return false;
  for (const k of [enumKeyToCamel(featureKey), featureKey, featureKey.toLowerCase()]) {
    if (k in feature) return Boolean(feature[k]);
  }
  return false;
}

/**
 * `getRemoteControlLink` reply → the viewer URL. open-api returns `{ viewerLink }` (staff-api
 * JSON-stringifies it); older shapes were a bare string or `{ url }`.
 */
export function parseRemoteLink(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === 'string') return parsed || null;
    if (parsed && typeof parsed === 'object') {
      const o = parsed as { viewerLink?: unknown; url?: unknown };
      if (typeof o.viewerLink === 'string' && o.viewerLink) return o.viewerLink;
      if (typeof o.url === 'string' && o.url) return o.url;
    }
    return null;
  } catch {
    return raw;
  }
}

/**
 * Minimal Apollo-error renderer. Mirrors scio3's `parseMdmError` shape
 * without duplicating its full rule set (which references screen-specific
 * error codes the staff console never surfaces). Apollo 4 no longer exports a
 * canonical `ApolloError` class, so we use a structural shape. Priority:
 *   1. graphQLErrors[0].extensions.originalError.message
 *   2. graphQLErrors[0].message
 *   3. apollo.networkError.message
 *   4. fallback string
 */
interface ApolloErrorLike {
  graphQLErrors?: Array<{ message?: string; extensions?: Record<string, unknown> }>;
  networkError?: { message?: string } | null;
  message?: string;
}

function parseMdmError(err: unknown, fallback: string): string {
  const apolloErr = err as ApolloErrorLike | undefined;
  const first = apolloErr?.graphQLErrors?.[0];
  const originalMessage = (
    first?.extensions as { originalError?: { message?: string } } | undefined
  )?.originalError?.message;
  if (originalMessage) return originalMessage;
  if (first?.message) return first.message;
  if (apolloErr?.networkError?.message) return apolloErr.networkError.message;
  return fallback;
}

/**
 * Catalog `i18nKey` values are flat English keys (e.g. "Reboot device").
 * With i18n dropped we use them verbatim as the human label.
 */
function commandLabel(cmd: MdmCommandDefinition): string {
  return cmd.i18nKey;
}

function toastOk(_ctx: MdmDispatchContext, cmd: MdmCommandDefinition): void {
  toast.success(`${commandLabel(cmd)} command sent`);
}

function toastErr(_ctx: MdmDispatchContext, cmd: MdmCommandDefinition, err: unknown): void {
  toast.error(parseMdmError(err, `Failed to send ${commandLabel(cmd)} command`));
}

// ---------------------------------------------------------------------------
// Handlers — one per MdmCommandAction.type
// ---------------------------------------------------------------------------

export const handleExecution: MdmActionHandler = async (cmd, ctx, targets) => {
  if (cmd.action.type !== 'execution') return;
  const deviceIds = getDeviceIds(ctx, targets);
  const wire = cmd.action.wireCommand;
  try {
    if (isBulk(targets)) {
      await ctx.mutations.addCommandExecutionGroup({ deviceIds, command: wire });
    } else {
      const reply = await ctx.mutations.addCommandExecution({
        deviceId: deviceIds[0] ?? '',
        command: wire,
      });
      // Storage Details is needs-response: open-api waits for the device and returns the numbers
      // inline (scio3 MdmButtonRow.handleGetStorage). The dialog shows them — or, when the reply came
      // back empty, falls back to the command-execution row's `result`.
      if (cmd.id === 'MDM_GET_USED_STORAGE') {
        ctx.openChildDialog('storageDetails', { device: ctx.device, reply });
        ctx.refetchDevice?.();
        return;
      }
    }
    toastOk(ctx, cmd);
    ctx.refetchDevice?.();
  } catch (err) {
    toastErr(ctx, cmd, err);
  }
};

export const handleMdmMutation: MdmActionHandler = async (cmd, ctx, targets) => {
  if (cmd.action.type !== 'mdm-mutation') return;
  const deviceIds = getDeviceIds(ctx, targets);
  const wire = cmd.action.wireCommand;
  try {
    await ctx.mutations.mdmCommand({ deviceIds, command: wire });
    toastOk(ctx, cmd);

    // Screenshot follow-up poll (spec §5.3).
    if (cmd.id === 'MDM_CAPTURE_SCREENSHOT' && ctx.refetchDevice) {
      const refetch = ctx.refetchDevice;
      [10_000, 20_000, 30_000].forEach((delay) => {
        setTimeout(() => refetch(), delay);
      });
    } else {
      ctx.refetchDevice?.();
    }
  } catch (err) {
    toastErr(ctx, cmd, err);
  }
};

export const handleFeatureToggle: MdmActionHandler = async (cmd, ctx, targets) => {
  if (cmd.action.type !== 'feature-toggle') return;
  const deviceIds = getDeviceIds(ctx, targets);
  const { featureKey } = cmd.action;

  const reference = Array.isArray(targets) ? targets[0] : (targets ?? ctx.device);
  const currentValue = readFeatureFlag(
    (reference as { feature?: Record<string, unknown> | null } | undefined)?.feature,
    featureKey,
  );
  const nextValue = !currentValue;

  try {
    await ctx.mutations.toggleFeature({
      deviceIds,
      featureKey,
      featureValue: nextValue,
    });
    toastOk(ctx, cmd);
    ctx.refetchDevice?.();
  } catch (err) {
    toastErr(ctx, cmd, err);
  }
};

export const handleOpenDialog: MdmActionHandler = async (cmd, ctx) => {
  if (cmd.action.type !== 'open-dialog') return;
  ctx.openChildDialog(cmd.action.dialogId, { device: ctx.device });
};

export const handleOpenRemoteViewer: MdmActionHandler = async (cmd, ctx) => {
  if (cmd.action.type !== 'open-remote-viewer') return;
  try {
    const link = await ctx.mutations.fetchRemoteLink(ctx.device._id);
    if (!link) {
      toast.error('Remote control link not available');
      return;
    }
    ctx.openChildDialog('remoteControl', { device: ctx.device, remoteUrl: link });
  } catch (err) {
    // Surface the backend's actionable message ("The Remote Agent is not started yet…") — the agent
    // can be installed but not running even with Remote Control on (scio3 #1543).
    toast.error(parseMdmError(err, 'Failed to get remote control link'));
  }
};

/**
 * `action.type === 'identify-screen'` — flashes the screen via the dedicated `identifyScreen`
 * mutation (server-side content swap to a flash-asset template for ~30s, then auto-reverts). NOT
 * `addCommandExecution`: 'identifyScreen' is not an execution command, so that path never worked.
 * Port of scio3's handleIdentifyScreen.
 */
export const handleIdentifyScreen: MdmActionHandler = async (cmd, ctx, targets) => {
  if (cmd.action.type !== 'identify-screen') return;
  const deviceIds = getDeviceIds(ctx, targets);
  try {
    await ctx.mutations.identifyScreen({ deviceIds });
    toastOk(ctx, cmd);
    ctx.refetchDevice?.();
  } catch (err) {
    toastErr(ctx, cmd, err);
  }
};

/**
 * `action.type === 'platform-api'` — dispatches vendor-cloud operations
 * (Zoom Rooms / Teams / Webex / Meet) via `platformApiCommand`. Lifted from
 * scio3's apps/web handler; see spec 07 §7.6. Staff console reuses this for
 * cross-tenant ops — backend enforces `authCtx.accountId` on the target
 * `mdm-devices` row + `AdminRoleGuard` on the resolver.
 *
 * `openAdminDeepLink` is special: adapter returns a URL in `response.data`
 * which we open in a new tab (Zoom Admin deep-link is unauthenticated from
 * our side — Zoom handles auth separately).
 */
export const handlePlatformApi: MdmActionHandler = async (cmd, ctx, targets) => {
  if (cmd.action.type !== 'platform-api') return;
  const deviceIds = getDeviceIds(ctx, targets);
  const { operation } = cmd.action;
  try {
    if (deviceIds.length > 1) {
      const results = await Promise.allSettled(
        deviceIds.map((id) => ctx.mutations.platformApiCommand({ deviceId: id, operation })),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed === 0) toastOk(ctx, cmd);
      else if (failed < results.length) {
        toast.warning(
          `${commandLabel(cmd)} completed on ${results.length - failed} of ${results.length} devices`,
        );
      } else {
        toastErr(ctx, cmd, new Error('All devices failed'));
      }
      ctx.refetchDevice?.();
      return;
    }

    const deviceId = deviceIds[0] ?? '';
    const response = await ctx.mutations.platformApiCommand({ deviceId, operation });
    if (!response.ok) {
      toastErr(ctx, cmd, new Error(response.error ?? 'Command failed'));
      return;
    }

    if (operation === 'openAdminDeepLink' && typeof response.data === 'string') {
      window.open(response.data, '_blank', 'noopener,noreferrer');
      return;
    }

    toastOk(ctx, cmd);
    ctx.refetchDevice?.();
  } catch (err) {
    toastErr(ctx, cmd, err);
  }
};

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export const MDM_ACTION_HANDLERS: Record<MdmCommandAction['type'], MdmActionHandler> = {
  execution: handleExecution,
  'mdm-mutation': handleMdmMutation,
  'feature-toggle': handleFeatureToggle,
  'open-dialog': handleOpenDialog,
  'open-remote-viewer': handleOpenRemoteViewer,
  'identify-screen': handleIdentifyScreen,
  'platform-api': handlePlatformApi,
};

// ---------------------------------------------------------------------------
// Dispatch context hook
// ---------------------------------------------------------------------------

interface UseMdmDispatchContextOptions {
  device: MdmDevice;
  /** Team the device lives in. Falls back to the team store's active team (empty on My Devices). */
  teamId?: string | null;
  refetchDevice?: () => void;
  /** Caller-provided dialog opener. Staff drawer wires to local state. */
  openChildDialog?: (dialogId: string, context?: Record<string, unknown>) => void;
  /**
   * Customer device drawer only: the page's account, sent with `staffProxyTestingIdentifyScreen`
   * (the one op here that takes it). Omitted everywhere else — the generic ops never take one.
   */
  accountId?: string;
}

/**
 * Wires Apollo mutations + team id + refetch + `openChildDialog` into a stable
 * `MdmDispatchContext`. Memoized so handler references stay stable across
 * renders.
 */
export function useMdmDispatchContext(options: UseMdmDispatchContextOptions): MdmDispatchContext {
  const { device, refetchDevice, openChildDialog, accountId } = options;
  const storeTeamId = useTeamStore((s) => s.activeTeamId);
  const activeTeamId = options.teamId || storeTeamId;

  const [mdmCommandFn] = useMutation(STAFF_PROXY_MDM_COMMAND);
  const [addCommandExecutionFn] = useMutation<{ staffProxyAddCommandExecution: string | null }>(
    STAFF_PROXY_ADD_COMMAND_EXECUTION,
  );
  const [addCommandExecutionGroupFn] = useMutation(STAFF_PROXY_ADD_COMMAND_EXECUTION_GROUP);
  const [toggleFeatureFn] = useMutation(STAFF_PROXY_TOGGLE_DEVICE_FEATURE);
  const [identifyScreenFn] = useMutation(STAFF_PROXY_TESTING_IDENTIFY_SCREEN);
  // platformApi + getRemoteControlLink return JSON-stringified strings (staff-api
  // keeps them decoupled from open-api enums). Wrappers below JSON.parse before
  // returning the structured shape the handlers expect.
  const [platformApiCommandFn] = useMutation<{
    staffProxyPlatformApiCommand: string;
  }>(STAFF_PROXY_PLATFORM_API_COMMAND);
  const [fetchRemoteLinkFn] = useLazyQuery<{
    staffProxyGetRemoteControlLink: string | null;
  }>(STAFF_PROXY_GET_REMOTE_CONTROL_LINK, { fetchPolicy: 'no-cache' });

  // Default opener: no-op with warn so callers notice when they forgot to
  // wire a handler but the UI doesn't crash. Production drawers should
  // always pass a real `openChildDialog`.
  const noopOpenDialog = useMemo(
    () => (id: string) => {
      // console.warn is permitted in error handlers — see general.md. Here
      // we use it as a developer-facing signal, not a user-visible one.
      console.warn(`[staff mdm] openChildDialog('${id}') — no handler wired`);
    },
    [],
  );

  return useMemo<MdmDispatchContext>(
    () => ({
      device,
      teamId: activeTeamId ?? null,
      refetchDevice,
      openChildDialog: openChildDialog ?? noopOpenDialog,
      mutations: {
        addCommandExecution: async ({ deviceId, command, value }) => {
          const { data } = await addCommandExecutionFn({
            variables: {
              payload: {
                teamId: activeTeamId ?? '',
                payloadJson: JSON.stringify({ deviceId, command, value: value ?? '' }),
                isNeedResponse: false,
              },
            },
          });
          const raw = data?.staffProxyAddCommandExecution;
          if (!raw) return null;
          try {
            return JSON.parse(raw) as unknown;
          } catch {
            return null;
          }
        },
        addCommandExecutionGroup: async ({ deviceIds, command, value }) => {
          await addCommandExecutionGroupFn({
            variables: {
              payload: {
                teamId: activeTeamId ?? '',
                payloadJson: JSON.stringify({ deviceIds, command, value: value ?? '' }),
              },
            },
          });
        },
        mdmCommand: async ({ deviceIds, command, value }) => {
          await mdmCommandFn({
            variables: {
              payload: { deviceIds, command, value },
            },
          });
        },
        toggleFeature: async ({ deviceIds, featureKey, featureValue }) => {
          await toggleFeatureFn({
            variables: {
              payload: {
                deviceIds,
                payloadJson: JSON.stringify({
                  deviceIds,
                  featureKey: featureKey.toUpperCase(),
                  featureValue,
                }),
              },
            },
          });
        },
        fetchRemoteLink: async (deviceId) => {
          const { data } = await fetchRemoteLinkFn({
            variables: { payload: { teamId: activeTeamId ?? '', deviceId } },
          });
          return parseRemoteLink(data?.staffProxyGetRemoteControlLink);
        },
        platformApiCommand: async ({ deviceId, operation, args }) => {
          const { data } = await platformApiCommandFn({
            variables: {
              payload: {
                deviceId,
                operation,
                argsJson: args ? JSON.stringify(args) : undefined,
              },
            },
          });
          const raw = data?.staffProxyPlatformApiCommand;
          if (!raw) return { ok: false, data: null, error: 'No response' };
          try {
            const parsed = JSON.parse(raw) as {
              ok?: boolean;
              data?: unknown;
              error?: string | null;
            };
            return {
              ok: Boolean(parsed?.ok),
              data: parsed?.data,
              error: parsed?.error ?? null,
            };
          } catch {
            return { ok: false, data: null, error: 'Malformed response' };
          }
        },
        identifyScreen: async ({ deviceIds }) => {
          // staffProxyTestingIdentifyScreen is single-device; the drawer never bulk-dispatches.
          for (const deviceId of deviceIds) {
            // Customer drawer only: the page's account (staff-api's customer gate). Testing mode
            // omits the key entirely, so its request is unchanged.
            await identifyScreenFn({ variables: { deviceId, ...(accountId ? { accountId } : {}) } });
          }
        },
      },
    }),
    [
      device,
      activeTeamId,
      refetchDevice,
      openChildDialog,
      noopOpenDialog,
      addCommandExecutionFn,
      addCommandExecutionGroupFn,
      mdmCommandFn,
      toggleFeatureFn,
      fetchRemoteLinkFn,
      platformApiCommandFn,
      identifyScreenFn,
      accountId,
    ],
  );
}
